/**
 * Namespaced, level-filtered logger with a ring buffer + live subscription
 * so an in-app DebugPanel can tail entries without DevTools.
 *
 * Usage
 *   const log = debug('brain:memory');
 *   log.info('create', { scope, kind });
 *   log.warn('create failed', { error, scope });
 *   log.error('fatal', err);               // auto-runs formatError on err
 *
 * Filter (localStorage):
 *   4cm_debug        — namespace glob list, comma-separated
 *                        '*'                        → everything
 *                        '3d:*,brain:memory'        → only these
 *                        '-canvas:sync,*'           → all except canvas:sync
 *                        ''                         → nothing (default, prod-safe)
 *   4cm_debug_level  — floor level: 'error'|'warn'|'info'|'debug'|'trace'
 *                        default 'info'
 *   4cm_debug_buffer — ring buffer size, default 500
 *
 * No logs render unless a namespace matches. That means this module is safe
 * to leave wired up in production — zero noise when the flag is empty.
 *
 * Entries are also pushed to an in-memory ring so `getLogs()` and `onLog(cb)`
 * can feed the DebugPanel. Ring never persists across reloads.
 */

import { getActiveTrace } from './trace';

export type LogLevel = 'error' | 'warn' | 'info' | 'debug' | 'trace';

const LEVEL_RANK: Record<LogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
  trace: 4,
};

const LEVEL_COLOR: Record<LogLevel, string> = {
  error: '#e74c3c',
  warn: '#f39c12',
  info: '#3498db',
  debug: '#95a5a6',
  trace: '#bdc3c7',
};

export interface LogEntry {
  ts: number;
  level: LogLevel;
  ns: string;
  msg: string;
  data?: unknown;
  trace: string | null;
}

export interface Logger {
  error: (msg: string, data?: unknown) => void;
  warn: (msg: string, data?: unknown) => void;
  info: (msg: string, data?: unknown) => void;
  debug: (msg: string, data?: unknown) => void;
  trace: (msg: string, data?: unknown) => void;
}

/** Namespaces known to the app. The DebugPanel uses this for autocomplete.
 *  Loose — adding a new `debug('x:y')` call doesn't require adding it here. */
export const KNOWN_NAMESPACES = [
  'ai',
  'ai:json',
  'ai:surface',
  'ai:retry',
  'ai:tools',
  'ai:vision',
  'brain:events',
  'brain:events:handler',
  'brain:memory',
  'brain:tools',
  'brain:summarize',
  'brain:suggestions',
  'canvas:load',
  'canvas:save',
  'canvas:sync',
  '3d:playback',
  '3d:step',
  '3d:scene',
  'supabase:query',
  'supabase:error',
  'video:analyse',
  'video:capture',
  'review:suggest',
  'review:autofill',
  'roster:load',
  'roster:autogen',
  'philosophy:suggest',
  'curriculum:gen',
  'curriculum:commit',
  'voice:onboarding',
  'voice:canvas',
  'voice:drill',
  'voice:problem',
  'now:plan',
  'session:detail',
  'session:planner',
  'diagram:gen',
] as const;

// ---------- state ----------
//
// Hoisted onto globalThis behind a Symbol.for key so that multiple copies of
// this module (e.g. when Vite / pnpm ends up with two instances of
// @4cm/shared across workspace packages) still share one ring buffer and
// one listener set. Otherwise the DebugPanel's `onLog` subscribes to one
// copy while `debug().info(...)` pushes into the other, and the panel
// renders empty even though Console is full.

const DEFAULT_BUFFER_SIZE = 500;

interface GlobalStore {
  ring: LogEntry[];
  listeners: Set<(entry: LogEntry) => void>;
}
const GLOBAL_KEY = Symbol.for('@4cm/shared.debugStore');
const globalBag = globalThis as unknown as Record<symbol, GlobalStore | undefined>;
if (!globalBag[GLOBAL_KEY]) {
  globalBag[GLOBAL_KEY] = { ring: [], listeners: new Set() };
}
const store = globalBag[GLOBAL_KEY]!;
const ring: LogEntry[] = store.ring;
const listeners: Set<(entry: LogEntry) => void> = store.listeners;

let cachedMatchers: ReturnType<typeof buildMatchers> | null = null;
let cachedRaw = '__uninit__';
let cachedLevel: LogLevel = 'info';
let cachedLevelRaw = '__uninit__';

// ---------- filter parsing ----------

interface Matchers {
  includes: RegExp[];
  excludes: RegExp[];
  /** If true, '*' is in the list — include-all with per-exclude carve-outs. */
  includeAll: boolean;
}

function buildMatchers(raw: string): Matchers {
  const m: Matchers = { includes: [], excludes: [], includeAll: false };
  if (!raw) return m;
  const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
  for (const p of parts) {
    if (p === '*') {
      m.includeAll = true;
      continue;
    }
    const negate = p.startsWith('-');
    const pattern = negate ? p.slice(1) : p;
    const re = globToRegex(pattern);
    if (negate) m.excludes.push(re);
    else m.includes.push(re);
  }
  return m;
}

function globToRegex(glob: string): RegExp {
  // Escape regex specials except '*', then turn '*' into '.*'
  const escaped = glob.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  return new RegExp(`^${escaped}$`);
}

function readLocalStorage(key: string): string {
  try {
    return typeof localStorage !== 'undefined' ? (localStorage.getItem(key) ?? '') : '';
  } catch {
    return '';
  }
}

function getMatchers(): Matchers {
  const raw = readLocalStorage('4cm_debug');
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cachedMatchers = buildMatchers(raw);
  }
  return cachedMatchers ?? buildMatchers('');
}

function getLevel(): LogLevel {
  const raw = readLocalStorage('4cm_debug_level');
  if (raw === cachedLevelRaw) return cachedLevel;
  cachedLevelRaw = raw;
  cachedLevel =
    raw === 'error' || raw === 'warn' || raw === 'info' || raw === 'debug' || raw === 'trace'
      ? raw
      : 'info';
  return cachedLevel;
}

function getBufferSize(): number {
  const raw = readLocalStorage('4cm_debug_buffer');
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.min(n, 10000) : DEFAULT_BUFFER_SIZE;
}

function nsMatches(ns: string, m: Matchers): boolean {
  for (const re of m.excludes) {
    if (re.test(ns)) return false;
  }
  if (m.includeAll) return true;
  for (const re of m.includes) {
    if (re.test(ns)) return true;
  }
  return false;
}

// ---------- error extraction ----------

/** Unwrap anything into a human-readable message. Supabase errors show
 *  up as `{ message, code, details, hint }`; Anthropic errors as
 *  `{ error: { message, type } }`; fetch errors as `Response`; etc.
 *  Never returns the literal string "[object Object]". */
export function formatError(err: unknown): string {
  if (err == null) return String(err);
  if (typeof err === 'string') return err;
  if (err instanceof Error) {
    const parts = [err.message];
    if ('cause' in err && err.cause) parts.push(`cause: ${formatError(err.cause)}`);
    return parts.join(' · ');
  }
  if (typeof err === 'object') {
    const o = err as Record<string, unknown>;
    // Supabase: { message, code, details, hint }
    if (typeof o.message === 'string') {
      const bits: string[] = [o.message];
      if (typeof o.code === 'string' || typeof o.code === 'number') bits.push(`code=${o.code}`);
      if (typeof o.details === 'string' && o.details) bits.push(`details=${o.details}`);
      if (typeof o.hint === 'string' && o.hint) bits.push(`hint=${o.hint}`);
      return bits.join(' · ');
    }
    // Anthropic wrapper: { error: { message, type } }
    if (o.error && typeof o.error === 'object') {
      const inner = o.error as Record<string, unknown>;
      if (typeof inner.message === 'string') {
        const type = typeof inner.type === 'string' ? ` (${inner.type})` : '';
        return `${inner.message}${type}`;
      }
    }
    // Response-ish
    if (typeof o.status === 'number' && typeof o.statusText === 'string') {
      return `${o.status} ${o.statusText}`;
    }
    try {
      return JSON.stringify(err);
    } catch {
      return Object.prototype.toString.call(err);
    }
  }
  return String(err);
}

// ---------- core ----------

function push(entry: LogEntry): void {
  ring.push(entry);
  const cap = getBufferSize();
  const overflow = ring.length - cap;
  if (overflow > 0) ring.splice(0, overflow);
  for (const cb of listeners) {
    try {
      cb(entry);
    } catch {
      // A broken listener must not take out the logger.
    }
  }
}

function emitConsole(entry: LogEntry): void {
  const prefix = `%c${entry.ns}`;
  const style = `color:${LEVEL_COLOR[entry.level]};font-weight:600`;
  const traceTag = entry.trace ? ` ${entry.trace}` : '';
  const head = `${prefix}${traceTag}`;
  const fn =
    entry.level === 'error'
      ? console.error
      : entry.level === 'warn'
      ? console.warn
      : entry.level === 'info'
      ? console.info
      : console.log;
  if (entry.data === undefined) fn(head, style, entry.msg);
  else fn(head, style, entry.msg, entry.data);
}

function write(ns: string, level: LogLevel, msg: string, data?: unknown): void {
  const matchers = getMatchers();
  if (!nsMatches(ns, matchers)) return;
  if (LEVEL_RANK[level] > LEVEL_RANK[getLevel()]) return;

  // If `data` is an Error or Error-shaped object, keep the object but
  // also pre-compute a readable message so the console line is useful.
  let payload = data;
  if (data !== undefined && isErrorish(data)) {
    payload = { message: formatError(data), raw: data };
  }

  const entry: LogEntry = {
    ts: Date.now(),
    level,
    ns,
    msg,
    data: payload,
    trace: getActiveTrace(),
  };
  push(entry);
  emitConsole(entry);
}

function isErrorish(x: unknown): boolean {
  if (x instanceof Error) return true;
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.message === 'string' ||
    (typeof o.error === 'object' && o.error !== null) ||
    (typeof o.status === 'number' && typeof o.statusText === 'string')
  );
}

/** Factory — returns one logger per namespace. Cheap to call repeatedly;
 *  callers typically `const log = debug('ns')` at module top and reuse. */
export function debug(ns: string): Logger {
  return {
    error: (msg, data) => write(ns, 'error', msg, data),
    warn: (msg, data) => write(ns, 'warn', msg, data),
    info: (msg, data) => write(ns, 'info', msg, data),
    debug: (msg, data) => write(ns, 'debug', msg, data),
    trace: (msg, data) => write(ns, 'trace', msg, data),
  };
}

// ---------- DebugPanel surface ----------

export interface LogFilter {
  /** Namespace glob list, same syntax as `4cm_debug`. Empty → no filter. */
  namespaces?: string;
  /** Minimum level (entries below are hidden). */
  level?: LogLevel;
  /** Only entries with this trace id. */
  trace?: string;
}

export function getLogs(filter?: LogFilter): LogEntry[] {
  if (!filter) return ring.slice();
  const m = filter.namespaces ? buildMatchers(filter.namespaces) : null;
  const levelFloor = filter.level ? LEVEL_RANK[filter.level] : LEVEL_RANK.trace;
  return ring.filter((e) => {
    if (m && !nsMatches(e.ns, m)) return false;
    if (LEVEL_RANK[e.level] > levelFloor) return false;
    if (filter.trace && e.trace !== filter.trace) return false;
    return true;
  });
}

export function clearLogs(): void {
  ring.length = 0;
}

export function onLog(cb: (entry: LogEntry) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Reset cached localStorage reads — call after toggling the flag in DevTools
 *  or the DebugPanel, so the next log call picks up the new filter. */
export function refreshDebugConfig(): void {
  cachedRaw = '__uninit__';
  cachedLevelRaw = '__uninit__';
  cachedMatchers = null;
}
